/**
 * Workspace Vidéo — SVD (image→vidéo), AnimateDiff (texte→vidéo) et
 * LTX-Video (les deux). Les champs s'adaptent au modèle choisi.
 */
import { APIClient } from "../../api/client.js";
import { accordion } from "../../components/Accordion.js";
import { executionPicker } from "../../components/ExecutionPicker.js";
import { generateButton } from "../../components/GenerateButton.js";
import { createReadinessPanel } from "../../components/ReadinessPanel.js";
import { toast } from "../../components/Toast.js";
import { h } from "../../components/dom.js";
import { runGeneration, serverGenerates } from "../../services/execution.js";
import type { WorkspaceDef } from "../../shell/types.js";
import { store } from "../../state/store.js";
import type { VideoGenerationRequest, VideoModel } from "../../types/index.js";
import { sourceImageUpload } from "../image/SourceUploads.js";

// Guidance the family was tuned for. AnimateDiff's base is an SD 1.5
// checkpoint, whose territory is ~7.5; 3.0 is SVD's own number and was being
// applied to all three. Kept in step with ANIMATEDIFF_GUIDANCE in
// app/features/video/schemas.py, which picks the same value server-side for a
// caller that names none.
const SVD_GUIDANCE = 3.0;
const ANIMATEDIFF_GUIDANCE = 7.5;

const MODELS: { value: VideoModel; label: string; hint: string }[] = [
	{
		value: "svd",
		label: "SVD — Image → Vidéo",
		hint: "Anime une photo : elle est obligatoire.",
	},
	{
		value: "animatediff",
		label: "AnimateDiff — Texte → Vidéo",
		hint: "Crée une courte vidéo depuis une description (32 images au plus).",
	},
	{
		value: "ltx",
		label: "LTX-Video — Texte et/ou Image",
		hint: "Depuis une description, ou en animant une photo si vous en donnez une (121 images au plus).",
	},
];

export const videoWorkspace: WorkspaceDef = {
	id: "video",
	title: "Vidéo",
	icon: "video",
	build(container) {
		container.appendChild(h("h2", { class: "workspace-title" }, "Génération de vidéos"));

		let model: VideoModel = "svd";

		// --- Champs fixes (reconstruits quand le modèle change) ---
		const source = sourceImageUpload({
			label: "Photo à animer",
		});
		const prompt = h("textarea", {
			placeholder: "Décrivez la vidéo…",
		}) as HTMLTextAreaElement;
		const negative = h("textarea", {
			placeholder: "Ce que vous ne voulez pas voir",
		}) as HTMLTextAreaElement;
		const numFrames = h("input", {
			type: "number",
			value: 25,
			min: 2,
			max: 256,
		}) as HTMLInputElement;
		const fps = h("input", { type: "number", value: 7, min: 1, max: 30 }) as HTMLInputElement;
		const steps = h("input", { type: "number", value: 25, min: 1, max: 100 }) as HTMLInputElement;
		const guidance = h("input", {
			type: "number",
			value: SVD_GUIDANCE,
			min: 0,
			max: 20,
			step: 0.5,
		}) as HTMLInputElement;
		const motion = h("input", { type: "number", value: 127, min: 1, max: 255 }) as HTMLInputElement;
		const width = h("input", {
			type: "number",
			value: 576,
			min: 256,
			max: 1024,
			step: 32,
		}) as HTMLInputElement;
		const height = h("input", {
			type: "number",
			value: 576,
			min: 256,
			max: 1024,
			step: 32,
		}) as HTMLInputElement;
		const seed = h("input", {
			type: "number",
			value: -1,
			step: 1,
			title: "-1 = aléatoire",
		}) as HTMLInputElement;
		const baseSelect = h("select") as HTMLSelectElement;
		const adapterSelect = h("select") as HTMLSelectElement;

		const promptGroup = field("Description", prompt);
		const negativeGroup = field("À éviter", negative);
		const motionGroup = field("Intensité du mouvement", motion, "De 1 (calme) à 255 (agité).");
		const animatediffGroup = h(
			"div",
			null,
			field("Modèle d'image de départ", baseSelect),
			field("Module de mouvement", adapterSelect)
		);
		const sourceWrapper = h("div", null, source.element);
		const numFramesGroup = field("Nombre d'images", numFrames);
		const hint = h("p", { class: "help-text" });

		const modelSelect = h(
			"select",
			{
				onchange: () => {
					model = modelSelect.value as VideoModel;
					applyModel();
				},
			},
			...MODELS.map((m) => h("option", { value: m.value }, m.label))
		) as HTMLSelectElement;

		const runButton = generateButton("Générer la vidéo", () => void generate());

		// What this family still needs on the paired machine (a download, a
		// licence), with its button -- the Library's own answer.
		const readiness = createReadinessPanel();

		container.append(
			field("Modèle vidéo", modelSelect),
			hint,
			sourceWrapper,
			promptGroup,
			// The same fold as the Image form: what a first video needs stays
			// on screen, the numbers a curious person tunes are one click away.
			accordion({
				title: "Réglages avancés",
				children: [
					negativeGroup,
					animatediffGroup,
					h("div", { class: "form-grid" }, field("Largeur", width), field("Hauteur", height)),
					h("div", { class: "form-grid" }, numFramesGroup, field("Images par seconde", fps)),
					h(
						"div",
						{ class: "form-grid" },
						field("Étapes", steps),
						field("Guidage", guidance),
						motionGroup,
						field("Graine (-1 = au hasard)", seed)
					),
				],
			}).element,
			readiness.element,
			executionPicker(),
			runButton.element
		);

		function field(label: string, control: HTMLElement, help?: string): HTMLElement {
			return h(
				"div",
				{ class: "form-group" },
				h("label", { class: "field-label" }, label),
				control,
				help ? h("p", { class: "help-text" }, help) : null
			);
		}

		function applyModel(): void {
			const def = MODELS.find((m) => m.value === model);
			hint.textContent = def?.hint ?? "";
			promptGroup.classList.toggle("hidden", model === "svd");
			negativeGroup.classList.toggle("hidden", model !== "animatediff");
			motionGroup.classList.toggle("hidden", model !== "svd");
			// AnimateDiff ignores a photo: not asked for at all.
			sourceWrapper.classList.toggle("hidden", model === "animatediff");
			animatediffGroup.classList.toggle("hidden", model !== "animatediff");
			if (model === "animatediff") numFrames.max = "32";
			else if (model === "ltx") numFrames.max = "121";
			else numFrames.max = "256";
			// 3.0 is SVD's number and this field held it for all three families.
			// AnimateDiff rides on an SD 1.5 base, whose territory is ~7.5, and
			// it is visibly sharper there (measured on three scenes). The
			// backend picks the same default when a caller names none, but this
			// page always sends the field, so without this line that default
			// would never be reached from the only surface most people use.
			guidance.value = String(model === "animatediff" ? ANIMATEDIFF_GUIDANCE : SVD_GUIDANCE);
			readiness.show(model);
		}

		async function loadAnimatediffCatalogs(): Promise<void> {
			try {
				const [bases, adapters] = await Promise.all([
					APIClient.getAnimatediffBases(),
					APIClient.getAnimatediffMotionAdapters(),
				]);
				baseSelect.replaceChildren(
					...bases.map((b) =>
						h("option", { value: b.id }, `${b.name}${b.is_downloaded ? "" : " (à télécharger)"}`)
					)
				);
				adapterSelect.replaceChildren(
					...adapters.map((a) =>
						h("option", { value: a.id }, `${a.name}${a.is_downloaded ? "" : " (à télécharger)"}`)
					)
				);
			} catch {
				// Catalogue indisponible : les selects restent vides.
			}
		}
		void loadAnimatediffCatalogs();
		applyModel();

		async function generate(): Promise<void> {
			const initRef = source.getRef();
			if (model === "svd" && !initRef) {
				toast("SVD exige une image source.", "warning");
				return;
			}
			if ((model === "animatediff" || model === "ltx") && !prompt.value.trim()) {
				toast("Décrivez d'abord la vidéo souhaitée dans le prompt.", "warning");
				return;
			}
			if (model === "animatediff" && !baseSelect.value) {
				toast("Choisissez un checkpoint de base SD1.5 pour AnimateDiff.", "warning");
				return;
			}

			const payload: Record<string, unknown> = {
				model,
				prompt: prompt.value,
				negative_prompt: negative.value,
				init_image: initRef ?? undefined,
				base_model_id: model === "animatediff" ? baseSelect.value : undefined,
				motion_adapter_id: model === "animatediff" ? adapterSelect.value || undefined : undefined,
				width: Number(width.value) || 576,
				height: Number(height.value) || 576,
				num_frames: Number(numFrames.value) || 25,
				fps: Number(fps.value) || 7,
				motion_bucket_id: model === "svd" ? Number(motion.value) || 127 : undefined,
				num_inference_steps: Number(steps.value) || 25,
				guidance_scale: Number(guidance.value) || 3.0,
				seed: Number(seed.value) === -1 ? undefined : Number(seed.value),
			};
			try {
				await runGeneration({
					kind: "video",
					payload,
					hostedFailureMessage: "La génération vidéo sur votre ordinateur a échoué.",
					runLocal: async () => {
						const response = await APIClient.generateVideo(
							payload as unknown as VideoGenerationRequest
						);
						if (!response.success || !response.video_url) {
							toast(response.error ?? "Échec de la génération vidéo.", "error");
							return;
						}
						store.setState({
							currentResult: {
								kind: "video",
								url: response.video_url,
								filename: response.filename ?? "video.mp4",
								generationTime: response.generation_time ?? undefined,
							},
							historyVersion: store.get().historyVersion + 1,
						});
					},
				});
			} catch (error) {
				toast(error instanceof Error ? error.message : "Échec de la génération vidéo.", "error");
			}
		}
	},
};
